import { useContext, useEffect, useState } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import mixpanel from 'mixpanel-browser'
import moment from 'moment'
import { Search } from '@material-ui/icons' // eslint-disable-line no-restricted-imports
import { useFlags } from 'domains/launchdarkly/hooks'
import { useLDClient } from 'launchdarkly-react-client-sdk'
import { GET_ME_QUERY } from 'domains/carrotGraph/queries'
import { WithTheme } from 'app/WithTheme'
import { MaintenanceNotification } from 'ui/clinicianScreens/Maintenance/MaintenanceNotification'
import ErrorMessage from 'ui/components/ErrorMessage'
import { get } from 'nested-property'
import {
  ConfigContext,
  MembershipContext,
  OrgContext,
  UserContext,
  UserPHIContext,
} from 'ui/contexts'
import { SECOND_IN_MILLISECONDS } from 'utilities/time'
import {
  identifyLoggedInUserForTracking,
  registerMixpanelSuperProperties,
  setPropertiesOnUserProfile,
} from 'domains/mixpanel'
import { ANALYTICS_TRACKING_STATUS } from 'domains/mixpanel/consts'
import '../../variables.css'
import { AccessDeniedNotification } from 'ui/clinicianScreens/Maintenance/AccessDeniedNotification'
import { useLogger } from 'ui/hooks'
import useBoundStore from 'domains/zustand/store'
import Loading from 'ui/components/Loading'
import { Stack } from 'ui/baseComponents'

/**
 * Function that fetches further pages of memberships based on a cursor
 *
 * @param {function} fetchMore the function to append to the user query
 * @param {boolean} cursor the cursor indicating the page depth of our query
 */

export const fetchAllMemberships = (fetchMore, cursor) => {
  fetchMore({
    variables: {
      cursor,
    },
    updateQuery: (previousResult, { fetchMoreResult }) => {
      // isolates the list to which we are appending
      const prevMemberships =
        get(previousResult, 'user.membershipList.memberships') || []
      const newMemberships =
        get(fetchMoreResult, 'user.membershipList.memberships') || []
      // spread operator used repeatedly to append data to only the correct list.
      // A little bit verbose at this depth but necessary for our db structure.
      return {
        ...fetchMoreResult,
        user: {
          ...fetchMoreResult.user,
          membershipList: {
            ...fetchMoreResult.user.membershipList,
            memberships: [...prevMemberships, ...newMemberships],
          },
        },
      }
    },
  })
}

// Refetch query for User (used when switching orgs)
export const useRefetchMe = () => ({ query: GET_ME_QUERY })

/**
 * @returns {JSX.Element} App view for users with active sessions.
 */
function Root() {
  const [loadingComplete, setLoadingComplete] = useState(false)
  const [mixpanelLoaded, setMixpanelLoaded] = useState(false)
  const { vendors = {} } = useContext(ConfigContext)
  const { blockedUser, dhpMaintenanceUpcoming } = useFlags()
  const { pathname } = useLocation()
  const logger = useLogger()
  const ldClient = useLDClient()
  const signOut = useBoundStore((state) => state.signOut)

  const { data: me, loading, error, fetchMore } = useQuery(GET_ME_QUERY)

  const membershipsCursor = get(me, 'user.membershipList.pageInfo.endCursor')
  const phiVisibility = !!me?.user?.defaultMembership?.role?.canSeePHI

  useEffect(() => {
    if (me?.user?.membershipList && membershipsCursor) {
      fetchAllMemberships(fetchMore, membershipsCursor)
    }
  }, [fetchMore, me?.user?.membershipList, membershipsCursor])

  useEffect(() => {
    /*
     * Configure integrations that expect session user context
     */
    if (!me?.user) {
      return
    }

    /* Initialize LaunchDarkly with user */
    if (ldClient) {
      ldClient
        .identify({
          key: me.user.username,
          name: me.user.displayName,
          email: me.user.email,
        })
        .then(() => {
          setLoadingComplete(true)
        })
    } else {
      setLoadingComplete(true)
    }

    /* Initialize Mixpanel
        There is a Cypress bug that makes Cypress crash when cy.clock is used with mixpanel.
        See: https://github.com/cypress-io/cypress/issues/23370
        Until the bug is resolved, we need to make sure we're not in Cypress whenever we call a method on mixpanel.
    */
    if (!window.Cypress && !vendors.mixpanel?.disabled) {
      mixpanel.init(vendors.mixpanel.projectToken, {
        debug: process.env.NODE_ENV === 'development',
        loaded: setMixpanelLoaded(true),
      })
    }
  }, [me, ldClient, vendors.mixpanel, vendors.segment, setLoadingComplete])

  useEffect(() => {
    if (!me?.user || !mixpanelLoaded) {
      return
    }

    const {
      user: { analyticsTracking },
    } = me

    if (
      !window.Cypress &&
      analyticsTracking === ANALYTICS_TRACKING_STATUS.optedIn
    ) {
      // Opt the user in to data tracking and cookies/localstorage for this Mixpanel instance
      mixpanel.opt_in_tracking()

      identifyLoggedInUserForTracking(me.user.id)
      registerMixpanelSuperProperties(me.user)
      setPropertiesOnUserProfile(me.user)
    }

    if (
      !window.Cypress &&
      (analyticsTracking === ANALYTICS_TRACKING_STATUS.noDecision ||
        analyticsTracking === ANALYTICS_TRACKING_STATUS.optedOut)
    ) {
      /*  Opt the user out of data tracking and cookies/localstorage for this Mixpanel instance.
          If we don't explicitly opt out the user, when we call mixpanel.track we'll see bugs/pages breaking. */
      mixpanel.opt_out_tracking()
    }
  }, [me, mixpanelLoaded])

  // This useEffect enables us to track page views throughout the app
  useEffect(() => {
    if (!mixpanelLoaded) {
      return
    }

    let path = pathname

    const regexPatternWithNestedRoutes = /\/patients\/[^/]*\//
    const regexPatternWithoutNestedRoutes = /\/patients\/.*?.*/

    if (pathname.startsWith('/patients/')) {
      path = pathname.match(regexPatternWithNestedRoutes)
        ? pathname.replace(regexPatternWithNestedRoutes, '/patients/id/')
        : pathname.replace(regexPatternWithoutNestedRoutes, '/patients/id?')
    }

    mixpanel.track('Page View', {
      description:
        'tracks when a user navigates to a new page. Can see which page the user has visited.',
      page: path,
    })
  }, [mixpanelLoaded, pathname])

  if (error) {
    logger.error(error)

    const ERROR_401 = 401
    const ERROR_403 = 403

    if (
      error.networkError &&
      (error.networkError.statusCode === ERROR_401 ||
        error.networkError.statusCode === ERROR_403)
    ) {
      signOut()
      return <Navigate to="signin" replace />
    } else {
      return <ErrorMessage icon={Search} />
    }
  }

  if (!me?.user || loading || loadingComplete === false) {
    return (
      <Stack justifyContent="center" height="100vh">
        <Loading />
      </Stack>
    )
  }

  const startTime = dhpMaintenanceUpcoming?.start_time
    ? new Date(
        moment(dhpMaintenanceUpcoming.start_time * SECOND_IN_MILLISECONDS),
      )
    : null
  const endTime = dhpMaintenanceUpcoming?.end_time
    ? new Date(moment(dhpMaintenanceUpcoming.end_time * SECOND_IN_MILLISECONDS))
    : null

  return (
    <UserContext.Provider value={me.user}>
      <MembershipContext.Provider value={me.user.defaultMembership}>
        <OrgContext.Provider value={me.user.defaultMembership.org}>
          <UserPHIContext.Provider value={phiVisibility}>
            <WithTheme>
              {startTime &&
              endTime &&
              new Date() >= startTime &&
              new Date() < endTime ? (
                <MaintenanceNotification endTime={endTime} />
              ) : blockedUser ? (
                <AccessDeniedNotification />
              ) : (
                <Outlet />
              )}
            </WithTheme>
          </UserPHIContext.Provider>
        </OrgContext.Provider>
      </MembershipContext.Provider>
    </UserContext.Provider>
  )
}

export default Root
